export type PageType = 'product' | 'category_tab' | 'subcategory_tab' | 'category_landing' | 'hub' | 'news' | 'other';

export type TrafficMode = 'strict_page' | 'site_wide';

export type PageOption = {
  pageUrl: string;
  label: string;
  pageType: PageType;
  category: string | null;
  keywordCount: number;
};

const LEGACY_SLUG_CATEGORIES: Record<string, string> = {
  'polypropylene-pp': 'PP',
  'high-density-polyethylene-hdpe': 'HD',
  'linear-low-density-polyethylene-lldpe': 'LL',
  'low-density-polyethylene-ldpe': 'LD',
  'polyvinyl-chloride-pvc': 'PVC',
  'ethylene-vinyl-acetate-eva': 'EVA',
};

const CATEGORY_LABELS: Record<string, string> = {
  PP: 'PP',
  HD: 'HDPE',
  LD: 'LDPE',
  LL: 'LLDPE',
  PVC: 'PVC',
  EVA: 'EVA',
  HM: 'HM',
};

export const PAGE_TYPE_LABELS: Record<PageType, string> = {
  product: 'Product page',
  category_tab: 'Category tab',
  subcategory_tab: 'Subcategory tab',
  category_landing: 'Category landing',
  hub: 'Hub / homepage',
  news: 'News',
  other: 'Other URL',
};

export const normalizeUrl = (url: string): string => url.trim().replace(/\/$/, '');

const titleCaseSlug = (slug: string): string => (
  slug
    .replace(/-\d+$/, '')
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
);

export const classifyPageUrl = (rawUrl: string): Pick<PageOption, 'pageType' | 'category' | 'label'> => {
  try {
    const parsed = new URL(rawUrl);
    const path = parsed.pathname.replace(/\/$/, '') || '/';
    const categoryParam = parsed.searchParams.get('category')?.trim().toUpperCase() || null;

    if (path === '/' || path === '/about' || path === '/contact' || path === '/sell') {
      const label = path === '/' ? 'Homepage' : titleCaseSlug(path.slice(1));
      return { pageType: 'hub', category: null, label };
    }

    if (path === '/prices' && categoryParam) {
      const subcategoryParam = parsed.searchParams.get('subcategory');
      if (subcategoryParam) {
        return {
          pageType: 'subcategory_tab',
          category: categoryParam,
          label: `${CATEGORY_LABELS[categoryParam] || categoryParam} subcategory ${subcategoryParam}`,
        };
      }
      return {
        pageType: 'category_tab',
        category: categoryParam,
        label: `${CATEGORY_LABELS[categoryParam] || categoryParam} prices tab`,
      };
    }

    if (path === '/prices' || path === '/products') {
      return {
        pageType: 'hub',
        category: null,
        label: path === '/prices' ? 'Live prices hub' : 'Products index',
      };
    }

    if (path.startsWith('/news')) {
      const slug = path.split('/').filter(Boolean).slice(1).join('/') || 'news hub';
      return {
        pageType: 'news',
        category: null,
        label: slug === '' ? 'Credco Wire hub' : titleCaseSlug(slug),
      };
    }

    const productMatch = path.match(/^\/prices\/([a-z]{2,4})\/(.+)-(\d+)$/i);
    if (productMatch) {
      const category = productMatch[1].toUpperCase();
      return {
        pageType: 'product',
        category,
        label: titleCaseSlug(productMatch[2]),
      };
    }

    const segments = path.split('/').filter(Boolean);
    if (segments.length === 1) {
      const slug = segments[0].toLowerCase();
      const category = LEGACY_SLUG_CATEGORIES[slug] || null;
      if (category) {
        return {
          pageType: 'category_landing',
          category,
          label: `${CATEGORY_LABELS[category] || category} landing`,
        };
      }
    }

    return {
      pageType: 'other',
      category: categoryParam,
      label: path,
    };
  } catch (_error) {
    return { pageType: 'other', category: null, label: rawUrl };
  }
};

export const buildPageOptions = (
  rows: Array<{ page_url: string | null; keyword_text: string; is_active: boolean }>,
): PageOption[] => {
  const grouped = new Map<string, PageOption>();

  for (const row of rows) {
    if (!row.is_active || !row.page_url) continue;
    const pageUrl = normalizeUrl(String(row.page_url));
    if (!pageUrl) continue;

    const existing = grouped.get(pageUrl);
    if (existing) {
      existing.keywordCount += 1;
      continue;
    }

    const classified = classifyPageUrl(pageUrl);
    grouped.set(pageUrl, {
      pageUrl,
      keywordCount: 1,
      ...classified,
    });
  }

  return Array.from(grouped.values()).sort((a, b) => {
    if (a.pageType !== b.pageType) return a.pageType.localeCompare(b.pageType);
    if ((a.category || '') !== (b.category || '')) {
      return (a.category || '').localeCompare(b.category || '');
    }
    if (b.keywordCount !== a.keywordCount) return b.keywordCount - a.keywordCount;
    return a.label.localeCompare(b.label);
  });
};

export const filterPageOptions = (
  options: PageOption[],
  pageTypeFilter: PageType | 'all',
  categoryFilter: string,
): PageOption[] => (
  options.filter((option) => {
    if (pageTypeFilter !== 'all' && option.pageType !== pageTypeFilter) return false;
    if (categoryFilter !== 'all' && option.category !== categoryFilter) return false;
    return true;
  })
);

export const listCategories = (options: PageOption[]): string[] => {
  const set = new Set<string>();
  for (const option of options) {
    if (option.category) set.add(option.category);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};
